import { createApp, nextTick, type AppContext, type Component } from "vue";

/**
 * Prints a Vue component: a hidden iframe takes over the app's style sheets
 * (the kit, the tokens, the runtime look), mounts the component, waits for its
 * images, opens the browser's print dialog and removes itself. Nothing of the
 * page the user sees changes. The pattern of mail's print of a message,
 * lifted so every app prints the same way.
 *
 * The component is presentational: everything it shows comes in as props (an
 * object URL for an image the app loaded itself — revoke it after the promise
 * resolves). To let it use what the host app provides (vue-i18n, the kit's
 * injections) pass `appContext`, e.g. `getCurrentInstance()!.appContext`.
 */
export interface PrintComponentOptions {
  /** The document's title: the print dialog's file name. */
  title?: string;
  /** The host app's context, so the component sees its plugins and provides. */
  appContext?: AppContext;
  /** Longest wait for the images and fonts before printing anyway. Default 5000 ms. */
  readyTimeoutMs?: number;
  /** Replaces `window.print` of the frame (tests, previews). */
  print?: (frame: Window) => void | Promise<void>;
}

const DEFAULT_READY_TIMEOUT_MS = 5000;

/** Copies the style sheets and the look-carrying attributes of the page into the frame's document. */
function takeOverStyles(source: Document, target: Document): Promise<void>[] {
  const loads: Promise<void>[] = [];
  for (const name of [
    "lang",
    "class",
    "style",
    "data-theme",
    "data-density",
    "data-contrast",
  ]) {
    const value = source.documentElement.getAttribute(name);
    if (value !== null) target.documentElement.setAttribute(name, value);
  }
  for (const node of source.querySelectorAll('link[rel="stylesheet"], style')) {
    const copy = target.importNode(node, true) as HTMLElement;
    if (copy instanceof HTMLLinkElement) {
      loads.push(
        new Promise((resolve) => {
          copy.addEventListener("load", () => resolve(), { once: true });
          copy.addEventListener("error", () => resolve(), { once: true });
        }),
      );
    }
    target.head.append(copy);
  }
  return loads;
}

function imagesLoaded(root: ParentNode): Promise<void>[] {
  return [...root.querySelectorAll("img")]
    .filter((img) => !img.complete)
    .map(
      (img) =>
        new Promise<void>((resolve) => {
          img.addEventListener("load", () => resolve(), { once: true });
          img.addEventListener("error", () => resolve(), { once: true });
        }),
    );
}

function withTimeout(work: Promise<unknown>, ms: number): Promise<void> {
  return Promise.race([
    work.then(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, ms)),
  ]);
}

/**
 * Resolves once the print dialog is done with (or the frame could not print).
 * A user who cancels the dialog still resolves.
 */
export async function printComponent<P extends Record<string, unknown>>(
  component: Component,
  props: P = {} as P,
  options: PrintComponentOptions = {},
): Promise<void> {
  const frame = document.createElement("iframe");
  frame.title = options.title ?? "";
  frame.setAttribute("aria-hidden", "true");
  frame.style.cssText =
    "position:fixed;inset-inline-end:0;inset-block-end:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.append(frame);

  const doc = frame.contentDocument;
  const view = frame.contentWindow;
  if (!doc || !view) {
    frame.remove();
    return;
  }
  doc.open();
  doc.write(
    '<!doctype html><html><head><meta charset="utf-8"><title></title></head><body><div id="print-root"></div></body></html>',
  );
  doc.close();
  doc.title = options.title ?? "";

  const styleLoads = takeOverStyles(document, doc);
  doc.body.style.margin = "0";
  doc.body.style.background = "#fff";

  const app = createApp(component, props);
  if (options.appContext) {
    Object.assign(app._context.provides, options.appContext.provides);
    Object.assign(
      app.config.globalProperties,
      options.appContext.config.globalProperties,
    );
  }
  try {
    app.mount(doc.getElementById("print-root")!);
    await nextTick();
    const timeout = options.readyTimeoutMs ?? DEFAULT_READY_TIMEOUT_MS;
    await withTimeout(Promise.all(styleLoads), timeout);
    await withTimeout(Promise.all(imagesLoaded(doc)), timeout);
    await withTimeout(
      (doc as Document & { fonts?: { ready: Promise<unknown> } }).fonts
        ?.ready ?? Promise.resolve(),
      timeout,
    );
    if (options.print) {
      await options.print(view);
    } else if (!/jsdom/i.test(view.navigator?.userAgent ?? "")) {
      view.focus();
      // `print()` blocks until the dialog is closed.
      view.print();
    }
  } finally {
    app.unmount();
    frame.remove();
  }
}
